import {createAction} from '@reduxjs/toolkit';
import {OperationsSchemaV2} from '~/entities/operations-fiat';
import {HttpClient, expectEither} from '~/library/network-facade';
import {Decoder} from '@raison/prelude';
import {unSnakeByMutation} from '~/library/toolbox';
import selectUserAbility from '~/redux/access-control/selectUserAbility';
import {onlyLeading, withPayload} from '~/shared/utils/for-redux';
import {FinancialData} from './financesReducer';

export const operationsRequestRoutine = (type => ({
    aborted: createAction(`${type}/ABORTED`),
    trigger: createAction(`${type}/TRIGGER`),
    request: createAction(`${type}/REQUEST`),
    success: createAction(`${type}/SUCCESS`, withPayload<FinancialData>),
    failure: createAction(`${type}/FAILURE`, (error: unknown) => ({payload: error})),
}))('OPERATIONS_REQUEST' as const);

export default function init(startListening: AppStartListening) {
    startListening({
        actionCreator: operationsRequestRoutine.trigger,
        effect: onlyLeading(async (_, api) => {
            const user = selectUserAbility(api.getState());
            if (user.cannot('view', 'feature')) {
                api.dispatch(operationsRequestRoutine.aborted());
                return;
            } else {
                api.dispatch(operationsRequestRoutine.request());
            }
            const {status, json} = await operationsService(api.extra.http).catch(error => {
                api.dispatch(operationsRequestRoutine.failure(error));
                throw error;
            });
            if (status === 'Abort') {
                return;
            }
            api.dispatch(operationsRequestRoutine.success(json));
        }),
    });
}

export const operationsService = (http: HttpClient) =>
    http('v1/accounts/operations').then(
        ...expectEither({
            200: {
                async parse(res: HttpClient.SyncOutput) {
                    const json = unSnakeOperationsData(await res.json());
                    return Decoder.run(OperationsSchemaV2, json).orThrow();
                },
            },
        })
    );

const unSnakeOperationsData = (unparsed: Record<string, unknown>) => {
    const {operationsSettings, ...rest} = unparsed;
    unSnakeByMutation(rest);
    rest.operationsSettings = operationsSettings;
    return rest;
};
